/*
  VIP Status Cache Helper
  -----------------------
  Purpose:
  - Provide a small, shared cache layer for VIP status requests.
  - Reduce duplicate network calls and avoid rate-limit issues.
  - Keep cache scoped to the current tab (sessionStorage), with a short TTL.

  Design Notes (first principles):
  - Cache is a local, best-effort optimization and must never be a source of truth.
  - It should fail safely: if storage is unavailable or corrupted, behave as if cache is empty.
  - TTL exists to prevent stale UI (VIP state can change).
*/

const DEFAULT_TTL_MS = 60 * 1000; // 1 minute
const DEFAULT_KEY = "vipStatusCache";
import { getUser } from "./authStorage";

const DEFAULT_NAMESPACE = "global";

/**
 * Build a stable storage key, optionally namespaced (e.g., by userId/email).
 */
const buildStorageKey = (key, namespace) => {
  const safeKey = key || DEFAULT_KEY;
  const safeNamespace = namespace || DEFAULT_NAMESPACE;
  return `${safeKey}:${safeNamespace}`;
};

/**
 * Resolve a cache namespace using explicit namespace, user fields, or stored user.
 * This allows consistent caching across different components without passing user everywhere.
 */
export const resolveVipStatusNamespace = ({
  namespace,
  user,
  userStorageKey = "user",
} = {}) => {
  if (namespace) return namespace;

  const direct =
    user?.id || user?._id || user?.email || user?.username || user?.name;
  if (direct) return direct;

  try {
    let stored;

    if (userStorageKey === "user") {
      stored = getUser();
    } else {
      const raw = localStorage.getItem(userStorageKey);
      stored = raw ? JSON.parse(raw) : null;
    }

    if (!stored) return DEFAULT_NAMESPACE;

    const storedKey =
      stored?.id ||
      stored?._id ||
      stored?.email ||
      stored?.username ||
      stored?.name;
    return storedKey || DEFAULT_NAMESPACE;
  } catch {
    return DEFAULT_NAMESPACE;
  }
};

/**
 * Read cached VIP status if present and not expired.
 * Returns null if missing/expired/invalid.
 */
export const readVipStatusCache = ({
  key = DEFAULT_KEY,
  namespace = DEFAULT_NAMESPACE,
  ttlMs = DEFAULT_TTL_MS,
} = {}) => {
  try {
    const storageKey = buildStorageKey(key, namespace);
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const { value, timestamp } = parsed;
    if (typeof timestamp !== "number") return null;
    if (Date.now() - timestamp > ttlMs) return null;

    return value ?? null;
  } catch {
    return null;
  }
};

/**
 * Write VIP status to cache.
 */
export const writeVipStatusCache = ({
  value,
  key = DEFAULT_KEY,
  namespace = DEFAULT_NAMESPACE,
} = {}) => {
  try {
    const storageKey = buildStorageKey(key, namespace);
    const payload = { value, timestamp: Date.now() };
    sessionStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    // Ignore write failures (e.g., storage quota or disabled storage)
  }
};

/**
 * Clear VIP status cache.
 */
export const clearVipStatusCache = ({
  key = DEFAULT_KEY,
  namespace = DEFAULT_NAMESPACE,
} = {}) => {
  try {
    const storageKey = buildStorageKey(key, namespace);
    sessionStorage.removeItem(storageKey);
  } catch {
    // Ignore removal errors
  }
};

/**
 * In-flight protection helper:
 * - prevents overlapping network calls for the same key/namespace.
 * - resolves to the same promise while the fetch is in flight.
 *
 * Usage:
 *   await withVipStatusRequestLock({ namespace: userId }, () => fetchVipStatus())
 */
const inflightMap = new Map();

export const withVipStatusRequestLock = async (
  { key = DEFAULT_KEY, namespace = DEFAULT_NAMESPACE } = {},
  fetchFn,
) => {
  const storageKey = buildStorageKey(key, namespace);
  if (inflightMap.has(storageKey)) {
    return inflightMap.get(storageKey);
  }

  const promise = (async () => {
    try {
      return await fetchFn();
    } finally {
      inflightMap.delete(storageKey);
    }
  })();

  inflightMap.set(storageKey, promise);
  return promise;
};
